import React from "react"

import { FlexLayout } from "../layout/flex/FlexLayout"
import { LocalizedText } from "../text/LocalizedText"
import { ViewportCapsContext } from "../layout/viewport/ViewportCapsContext"
import { PlaySound } from "../sound/PlaySound"
import { Button } from "../button/Button"
import { ConfirmationFrame } from "../layout/frames/ConfirmationFrame"
import { HotKeyCode } from "../keyboard/HotKeyCode"

type ChromeVoiceSettingsModalStringKey =
  "caption" | "normalSpeedButton" | "doubleSpeedButton" | "testVoicePrompt" | "confirm" | "close"

/**
 * ChromeVoiceSettingsModalState tracks selected speed. This is required to highlight selected speed
 * option button in the modal.
 */
interface ChromeVoiceSettingsModalState {
  selectedSpeed: number
}

export type ChromeVoiceSettingsModalStrings = Partial<Record<ChromeVoiceSettingsModalStringKey,
  string>>

interface ChromeVoiceSettingsModalProps {
  /**
   * Current saved voice prompt speed in speechSynthesizer
   */
  savedSpeed?: number

  /**
   * Voice prompt speed options (https://tiny.amazon.com/c1n5sw2p/codeamazpackTaskblob1d15src)
   */
  speedOptions: number[]

  /**
   * Callback executed when user selects a speed option.
   */
  onSpeedSelect: (speed: number) => void

  /**
   * Callback executed when user clicks confirm button.
   */
  onConfirmButtonClick: (speed: number) => void

  /**
   * Callback executed when user clicks close button.
   */
  onCloseButtonClick: () => void
}

/**
 * Voice settings modal for selecting voice prompt speed.
 */
export class ChromeVoiceSettingsModal extends React.PureComponent<
    ChromeVoiceSettingsModalProps,
    ChromeVoiceSettingsModalState
  > {
  public static defaultStrings: ChromeVoiceSettingsModalStrings = {}

  public static readonly defaultTexts: Record<ChromeVoiceSettingsModalStringKey, string> = {
    caption: "Select Voice Prompt Speed",
    normalSpeedButton: "1.0x - Normal Speed",
    doubleSpeedButton: "2.0x - Double Speed",
    testVoicePrompt: "Test Prompt: 'Go to Aisle - X066D589'",
    confirm: "Confirm",
    close: "Close",
  }

  public static readonly hotKeys: Record<"confirm" | "close", HotKeyCode> = {
    confirm: "Enter",
    close: "KeyX",
  }

  public state: ChromeVoiceSettingsModalState = {
    selectedSpeed: this.props.savedSpeed || 1.0
  }

  /**
   * Stores callback functions for each speed to avoid creating new functions on every render.
   */
  private memoizedSpeedSelectCallbacks = new Map<number, () => void>()

  private getSpeedLabel(speed: number): { stringID?: string, defaultString: string } {
    const { defaultStrings, defaultTexts } = ChromeVoiceSettingsModal

    switch (speed) {
      case 1.0:
        return {
          stringID: defaultStrings.normalSpeedButton,
          defaultString: defaultTexts.normalSpeedButton
        }
      case 2.0:
        return {
          stringID: defaultStrings.doubleSpeedButton,
          defaultString: defaultTexts.doubleSpeedButton
        }
      default:
        return { defaultString: `${speed}x` }
    }
  }

  private getSpeedSelectCallback = (speed: number) => {
    if (!this.memoizedSpeedSelectCallbacks.has(speed)) {
      this.memoizedSpeedSelectCallbacks.set(speed, () => {
        this.props.onSpeedSelect(speed)
        this.setState({ selectedSpeed: speed })
      })
    }
    return this.memoizedSpeedSelectCallbacks.get(speed)!
  }

  private onConfirm = () => {
    this.props.onConfirmButtonClick(this.state.selectedSpeed)
  }

  private renderCaption(captionFontSize: "lg" | "xl") {
    const { defaultStrings, defaultTexts } = ChromeVoiceSettingsModal

    return (
      <FlexLayout.Item size="auto" paddingBottom="sm" dangerousClassName="voice-settings__caption">
        <LocalizedText
          size={captionFontSize}
          bold={true}
          stringID={defaultStrings.caption}
          variant="bright-blue"
          defaultString={defaultTexts.caption}
        />
      </FlexLayout.Item>
    )
  }

  private renderSpeedButtons(selectedSpeed: number, buttonSize: "md" | "xl") {
    return (
      <FlexLayout.Item dangerousClassName="voice-settings__speed-buttons">
        <FlexLayout direction="column">
          {this.props.speedOptions.map((speed) => {
            const buttonVariant = selectedSpeed === speed ? "primary" : "secondary"
            return (
              <FlexLayout.Item
                key={speed}
                paddingBottom="sm"
                dangerousClassName={`voice-settings__speed-${speed}_${buttonVariant}`}
              >
                <Button
                  flex={true}
                  size={buttonSize}
                  variant={buttonVariant}
                  onClick={this.getSpeedSelectCallback(speed)}
                  type="button"
                >
                  <LocalizedText {...this.getSpeedLabel(speed)} />
                </Button>
              </FlexLayout.Item>
            )
          })}
        </FlexLayout>
      </FlexLayout.Item>
    )
  }

  private renderTestVoicePrompt(captionFontSize: "lg" | "xl") {
    const { defaultStrings, defaultTexts } = ChromeVoiceSettingsModal
    return (
      <FlexLayout.Item size="auto" paddingTop="md" paddingBottom="lg" dangerousClassName="voice-settings__test-prompt">
        <LocalizedText
          size={captionFontSize}
          stringID={defaultStrings.testVoicePrompt}
          variant="bright-blue"
          defaultString={defaultTexts.testVoicePrompt}
        />
      </FlexLayout.Item>
    )
  }

  public render() {
    const { defaultStrings, defaultTexts, hotKeys } = ChromeVoiceSettingsModal

    return (
      <ViewportCapsContext.Consumer>
        {(viewportCaps) => {
          const captionFontSize = viewportCaps.hSizeAtMost("sm") ? "lg" : "xl"
          const buttonSize = viewportCaps.hSizeAtMost("md") ? "md" : "xl"

          return (
            <ConfirmationFrame
              primaryButtonLabel={{
                stringID: defaultStrings.confirm,
                defaultString: defaultTexts.confirm,
              }}
              primaryButtonHotKey={hotKeys.confirm}
              onPrimaryButtonClick={this.onConfirm}
              secondaryButtonLabel={{
                stringID: defaultStrings.close,
                defaultString: defaultTexts.close,
              }}
              secondaryButtonHotKey={hotKeys.close}
              onSecondaryButtonClick={this.props.onCloseButtonClick}
            >
              <PlaySound id="voiceSettings" sound="attention">
                <FlexLayout direction="column">
                  <FlexLayout.Item padding="sm">
                    <FlexLayout
                      direction="column"
                      padding="sm"
                      backgroundColor="white"
                      rounded={true}
                    >
                      {this.renderCaption(captionFontSize)}
                      {this.renderSpeedButtons(this.state.selectedSpeed, buttonSize)}
                      {this.renderTestVoicePrompt(captionFontSize)}
                    </FlexLayout>
                  </FlexLayout.Item>
                </FlexLayout>
              </PlaySound>
            </ConfirmationFrame>
          )
        }}
      </ViewportCapsContext.Consumer>
    )
  }
}
